import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import {
  createDateRangeFromNow,
  getDaysFromDateRange,
} from 'utils/dateTime'
import { getFirstPartOfURLPath } from 'utils/helpers'
import { Dropdown } from 'components/common/dropdown'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import { amplitudeEvent, UserEvents } from 'config/amplitude'
import { captureFilterApplied } from 'config/posthog/events'
import { FilterTypes } from 'config/posthog/constants'
import styles from './PeriodDropdown.module.scss'
import classNames from 'classnames/bind'
import { PeriodSelector } from 'components/common/performance-report/components/period-selector'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'

const cx = classNames.bind(styles)

export const LAST_30_DAYS = 0
export const CUSTOM_PERIOD = 1

const resolveCurrentPeriod = (period) => {
  const days = getDaysFromDateRange(period)
  return days.from === -30 && days.to === 0 ? LAST_30_DAYS : CUSTOM_PERIOD
}

const PeriodDropdown = ({
  period,
  onPeriodChange,
}) => {
  const { t } = useTranslation(['translations'])

  const { companyTimeZone } = useCompanyTimeZone()

  const selectBox = useRef(null)
  const [showCalendar, setShowCalendar] = useState(false)
  const [closeDropDown, setCloseDropDown] = useState(null)

  const periodValue = period ?? createDateRangeFromNow(-30, 0, companyTimeZone)
  const currentPeriod = resolveCurrentPeriod(periodValue)

  const items = [
    {
      item: 0,
      displayItem: t('widgets.polePriceAudit.components.periodDropdown.last30Days'),
    },
    {
      item: 1,
      displayItem: t('widgets.polePriceAudit.components.periodDropdown.customPeriod'),
    },
  ]

  const applyPeriod = selectedPeriod => {
    onPeriodChange(selectedPeriod)
    captureFilterApplied(FilterTypes.PERIOD)
  }

  const handleDropDownOpen = close => {
    amplitudeEvent(
      UserEvents.PerformanceReport.TIME_PERIOD_SELECTED(
        getFirstPartOfURLPath(window.location.pathname)
      )
    )
    if (currentPeriod === CUSTOM_PERIOD) {
      setImmediate(() => {
        setShowCalendar(true)
        setCloseDropDown(() => close)
      })
    }
  }

  const handleDropDownClose = () => {
    setShowCalendar(false)
    setCloseDropDown(null)
  }

  const handleItemClick = (event, close) => {
    const target = event.target.querySelector('span')
    const item = items.find(item => item.displayItem === target?.textContent)

    switch (item?.item) {
      case CUSTOM_PERIOD:
        event.preventDefault()
        setShowCalendar(true)
        setCloseDropDown(() => close)
        break
      case LAST_30_DAYS:
        applyPeriod(createDateRangeFromNow(-30, 0, companyTimeZone))
        break
      default:
        return
    }
  }

  const handlePeriodSelectorClose = period => {
    applyPeriod(period)
    closeDropDown()
  }

  const parseSelectedItem = () => {
    if (currentPeriod === CUSTOM_PERIOD) {
      const getDisplayValue = value => {
        return moment.tz(value, companyTimeZone).format('D MMM YYYY')
      }

      const { from, to } = periodValue
      const fromDisplayValue = getDisplayValue(from)
      const toDisplayValue = getDisplayValue(to)

      return {
        selectedItem: currentPeriod,
        selectedItemDisplay: `${fromDisplayValue} - ${toDisplayValue}`,
      }
    }
    return { selectedItem: currentPeriod, selectedItemDisplay: null }
  }

  const { selectedItem, selectedItemDisplay } = parseSelectedItem()

  return (
    <>
      <Dropdown
        selectedItem={selectedItem}
        items={items}
        selectedItemDisplay={selectedItemDisplay}
        onItemClick={handleItemClick}
        onOpen={handleDropDownOpen}
        onClose={handleDropDownClose}
        itemsRef={selectBox}
      />
      <Overlay
        target={selectBox.current}
        show={showCalendar}
        placement="left-start"
      >
        <Popover className={cx('popover')}>
          <PeriodSelector
            onClose={handlePeriodSelectorClose}
            currValue={periodValue}
            companyTimeZone={companyTimeZone}
            maxDays={30}
          />
        </Popover>
      </Overlay>
    </>
  )
}

PeriodDropdown.propTypes = {
  period: PropTypes.shape({
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
  }),
  onPeriodChange: PropTypes.func,
}

PeriodDropdown.defaultProps = {
  period: null,
}

export { PeriodDropdown }
